import { Box, Button, Grid, Theme, styled, useTheme } from '@mui/material'
import SearchInput from '@components/SearchInput'
import { memo, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Text, textStyles } from '@components/Helpers'
import { SlidersHorizontal } from 'lucide-react'
import VerticalDivider from '@components/VerticalDivider'
import { ExplorerContext } from '@context/Explorer/context'
import OverviewCheckbox from '@components/OverviewCheckbox'
import { DewiBreakpointsLg, DewiBreakpointsMd } from '@furry/tokens'
import { useSearchParams } from 'next/navigation'

const FilterButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsMd))]: {
    width: '2rem',
  },
  textTransform: 'capitalize',
  // The secondary pill: translucent, never outlined.
  borderRadius: 'var(--dewi_radius_rounded)',
  border: 'none',
  boxShadow: 'none',
  color: 'var(--dewi_colors_gray_0)',
  backgroundColor: 'var(--dewi_pill_secondary)',
  '&:hover': {
    backgroundColor: 'var(--dewi_pill_secondary_hover)',
  },
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    justifyContent: 'center',
    marginRight: '0.3rem',
  },
}))

const FilterIconGroup = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up(parseInt(DewiBreakpointsLg))]: {
    marginRight: 7,
  },
}))

const FilterButtonText = (theme: Theme) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
})

const FormVerticalDiv = (theme: Theme) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
})

const LargeSearchGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
}))

const SmallSearchGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
}))

function OverviewForm() {
  const { t } = useTranslation()
  const { handleToggleShowFilter, searchByIdHandler } =
    useContext(ExplorerContext)
  const theme = useTheme()
  const params = useSearchParams()

  const searchInputHandler = (value: string) => {
    const numeric = /^[0-9]*$/

    if (!value.match(numeric)) {
      return
    }
    searchByIdHandler(value)
  }

  const searchInputComponent = (
    <SearchInput value={params.get('id') || ''} onChange={searchInputHandler} />
  )

  return (
    <span className="flex flex-row w-full items-center justify-end">
      <OverviewCheckbox />
      <Grid item xs="auto">
        <FilterButton onClick={handleToggleShowFilter}>
          <FilterIconGroup>
            <SlidersHorizontal size={18} strokeWidth={1.75} />
          </FilterIconGroup>
          <Text sx={{ ...textStyles, ...FilterButtonText(theme) }}>
            {t('explorerPage.overview.form.filters')}
          </Text>
        </FilterButton>
      </Grid>
      <VerticalDivider sx={FormVerticalDiv(theme)} />
      <LargeSearchGrid item xs>
        {searchInputComponent}
      </LargeSearchGrid>
      <SmallSearchGrid item xs="auto">
        {searchInputComponent}
      </SmallSearchGrid>
    </span>
  )
}

export default memo(OverviewForm)
